
import { Suspense, lazy } from "react";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { AuthorityContentSection } from "@/components/AuthorityContentSection";
import { Features } from "@/components/Features";
import { Footer } from "@/components/Footer";
import { Services } from "@/components/Services";
import { TechnicalSEO } from "@/components/TechnicalSEO";
import { AIOptimizedStructuredData } from "@/components/AIOptimizedStructuredData";
import { AIContentMarkup } from "@/components/AIContentMarkup";
import { HomeGallery } from "@/components/HomeGallery";
import { InformationSection } from "@/components/InformationSection";
import { ProductTypesSection } from "@/components/ProductTypesSection";
import { ExternalResources } from "@/components/ExternalResources";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { InternalLinkingBoost } from "@/components/InternalLinkingBoost";
import NoiseComparison from "@/components/heritage/NoiseComparison";
import HeritageNeighbourhoodGrid from "@/components/heritage/HeritageNeighbourhoodGrid";
import AcousticTechSpecs from "@/components/heritage/AcousticTechSpecs";


// Lazy load non-critical components
const ServiceAreaMap = lazy(() => import("@/components/ServiceAreaMap").then(module => ({ default: module.ServiceAreaMap })));
const ProductShowcase = lazy(() => import("@/components/ProductShowcase").then(module => ({ default: module.ProductShowcase })));
const CaseStudies = lazy(() => import("@/components/CaseStudies").then(module => ({ default: module.CaseStudies })));
const QuoteCalculator = lazy(() => import("@/components/QuoteCalculator").then(module => ({ default: module.QuoteCalculator })));
const Testimonials = lazy(() => import("@/components/Testimonials").then(module => ({ default: module.Testimonials })));

export default function Index() {
  return (
    <main className="flex min-h-screen flex-col">
      <TechnicalSEO
        title="Secondary Glazing London | Acoustic & Heritage Experts"
        description="Bespoke secondary glazing in London for quieter, warmer period homes. Acoustic and heritage specialists offering surveys, installation and a 12-year warranty."
        canonical="https://www.londonsecondaryglazing.co.uk"
        ogImage="/lovable-uploads/b03c1cec-9fdc-4657-8503-0a9519f2834b.jpg"
        keywords="secondary glazing london, noise reduction windows, thermal insulation, heritage window solutions, energy efficient glazing, soundproof windows, acoustic glazing, double glazing alternative"
        pageType="homepage"
        breadcrumbs={[
          { name: "Home", url: "/" }
        ]}
      />
      
      {/* AI-Optimized Structured Data for Homepage */}
      <AIOptimizedStructuredData 
        type="Organization"
        data={{
          "@context": "https://schema.org",
           "@type": ["LocalBusiness", "HomeAndConstructionBusiness"],
           "@id": "https://www.londonsecondaryglazing.co.uk/#business",
           "parentOrganization": { "@id": "https://www.londonsecondaryglazing.co.uk/#organization" },
           "name": "London Secondary Glazing",
           "description": "Professional secondary glazing solutions in London for noise reduction up to 80%, thermal insulation, and heritage property preservation. Expert installation across Greater London.",
           "url": "https://www.londonsecondaryglazing.co.uk",
          "telephone": "+442070601572",
          "email": "info@londonsecondaryglazing.co.uk",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Arch 141, MacFarlane Road",
            "addressLocality": "London",
            "addressRegion": "Greater London",
            "postalCode": "W12 7LA",
            "addressCountry": "GB"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "51.5074",
            "longitude": "-0.1278"
          },
          "logo": {
            "@type": "ImageObject",
             "url": "https://www.londonsecondaryglazing.co.uk/og-image.png",
             "width": 1200,
             "height": 630
           },
           "image": "https://www.londonsecondaryglazing.co.uk/lovable-uploads/b03c1cec-9fdc-4657-8503-0a9519f2834b.jpg",
          "priceRange": "£££",
          "foundingDate": "2015",
          "openingHoursSpecification": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              "opens": "08:00",
              "closes": "18:00"
            },
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Saturday"],
              "opens": "09:00",
              "closes": "14:00"
            }
          ],
          "paymentAccepted": "Cash, Credit Card, Debit Card, Bank Transfer",
          "currenciesAccepted": "GBP",
          "slogan": "London's Trusted Secondary Glazing Specialists",
          "numberOfEmployees": {
            "@type": "QuantitativeValue",
            "minValue": "10",
            "maxValue": "50"
          },
          "areaServed": [
            {
              "@type": "GeoCircle",
              "geoMidpoint": {
                "@type": "GeoCoordinates",
                "latitude": "51.5074",
                "longitude": "-0.1278"
              },
              "geoRadius": "50000"
            }
          ],
          "sameAs": [
            "https://www.facebook.com/londonsecondaryglazing",
            "https://www.twitter.com/ldnsecondaryglazing",
            "https://www.instagram.com/londonsecondaryglazing",
            "https://www.linkedin.com/company/london-secondary-glazing"
          ],
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Secondary Glazing Services London",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Acoustic Secondary Glazing",
                  "description": "Professional noise reduction glazing achieving up to 80% noise reduction (45dB)",
                  "serviceType": "Secondary Glazing Installation"
                },
                "priceCurrency": "GBP",
                "priceRange": "£££",
                "availability": "https://schema.org/InStock"
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Thermal Secondary Glazing",
                  "description": "Energy efficient glazing reducing heat loss by up to 65%",
                  "serviceType": "Secondary Glazing Installation"
                },
                "priceCurrency": "GBP",
                "priceRange": "£££",
                "availability": "https://schema.org/InStock"
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Heritage Window Preservation",
                  "description": "Specialized secondary glazing for listed buildings and conservation areas",
                  "serviceType": "Heritage Window Services"
                },
                "priceCurrency": "GBP",
                "priceRange": "£££",
                "availability": "https://schema.org/InStock"
              }
            ]
          },
          "knowsAbout": [
            "Secondary Glazing",
            "Noise Reduction",
            "Thermal Insulation",
            "Heritage Properties",
            "Listed Buildings",
            "Conservation Areas",
            "Energy Efficiency",
            "Acoustic Treatment",
            "Window Renovation"
          ],
          "founder": {
            "@type": "Person",
            "name": "London Secondary Glazing Team"
          }
        }}
      />

      {/* Product Schema for Secondary Glazing */}
      <AIOptimizedStructuredData
        type="Product"
        data={{
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "Secondary Glazing Units",
          "description": "Professional secondary glazing units for London properties, delivering up to 80% noise reduction and significant thermal insulation improvements. Custom-made to fit any window.",
          "brand": {
            "@type": "Brand",
            "name": "London Secondary Glazing"
          },
          "image": "https://www.londonsecondaryglazing.co.uk/lovable-uploads/b03c1cec-9fdc-4657-8503-0a9519f2834b.jpg",
          "url": "https://www.londonsecondaryglazing.co.uk",
          "offers": {
            "@type": "AggregateOffer",
            "priceCurrency": "GBP",
            "lowPrice": "430",
            "highPrice": "1750",
            "offerCount": "7",
            "availability": "https://schema.org/InStock",
            "url": "https://www.londonsecondaryglazing.co.uk/get-quote"
          },
          "additionalProperty": [
            {
              "@type": "PropertyValue",
              "name": "Noise Reduction",
              "value": "Up to 80% (45dB)"
            },
            {
              "@type": "PropertyValue",
              "name": "Warranty",
              "value": "12 years"
            },
            {
              "@type": "PropertyValue",
              "name": "Service Area",
              "value": "Greater London"
            },
            {
              "@type": "PropertyValue",
              "name": "Thermal Efficiency Improvement",
              "value": "Up to 65%"
            }
          ]
        }}
      />
      
      <AIContentMarkup 
        contentType="homepage"
        title="Secondary Glazing London"
        description="Leading provider of secondary glazing solutions across London, specializing in noise reduction, thermal insulation, and heritage property preservation."
        category="Home Improvement Services"
        benefits={[
          "Up to 80% noise reduction (45dB)",
          "65% improvement in thermal efficiency",
          "Preserve original window character",
          "Cost-effective alternative to replacement",
          "Suitable for heritage and listed buildings",
          "Professional installation across London"
        ]}
        features={[
          "Custom-made to exact measurements",
          "Multiple operating systems available",
          "High-quality materials and finish",
          "Minimal disruption installation",
          "Free consultation and quote",
          "Comprehensive warranty coverage"
        ]}
        location="London, Greater London, UK"
      >
        <Navbar />
      <Hero />
      <AuthorityContentSection />
      <NoiseComparison />
      <Features />
      <AcousticTechSpecs />
      <Suspense fallback={<div className="h-[500px] flex items-center justify-center">Loading product showcase...</div>}>
        <ProductShowcase />
      </Suspense>
      
      {/* Services section with added internal links */}
      <Services />
      
      {/* Information Sections */}
      <InformationSection />
      
      <HomeGallery />
      <HeritageNeighbourhoodGrid />
      <Suspense fallback={<div className="h-96 flex items-center justify-center">Loading case studies...</div>}>
        <CaseStudies limit={3} />
      </Suspense>

      {/* Featured Location Guide */}
      <section className="py-12 bg-muted/30">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-primary text-center mb-2">Further Reading & Resources</h2>
          <p className="text-muted-foreground text-center mb-8 max-w-2xl mx-auto">In-depth guides for London's most prestigious neighbourhoods</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/marylebone-secondary-glazing-guide" className="group block rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow bg-background border border-border">
              <div className="h-44 overflow-hidden">
                <img src="/lovable-uploads/marylebone_georgian_sash_windows.jpg" alt="Georgian townhouse in Marylebone W1 with original sash windows" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" width="400" height="300" loading="lazy" decoding="async" />
              </div>
              <div className="p-4">
                <span className="text-xs font-semibold text-accent uppercase tracking-wider">Marylebone Guide</span>
                <h3 className="text-base font-semibold text-primary mt-1 group-hover:text-accent transition-colors">Secondary Glazing in Marylebone: Silence for W1</h3>
                <p className="text-sm text-muted-foreground mt-1">Harley Street privacy, Howard de Walden Estate compliance, and Regent's Park grandeur.</p>
              </div>
            </Link>
            <Link to="/chelsea-secondary-glazing-guide" className="group block rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow bg-background border border-border">
              <div className="h-44 overflow-hidden">
                <img src="/lovable-uploads/bedroom_secondary_glazing.png" alt="Red-brick Victorian townhouse in Chelsea SW3 with period sash windows" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" width="400" height="300" loading="lazy" decoding="async" />
              </div>
              <div className="p-4">
                <span className="text-xs font-semibold text-accent uppercase tracking-wider">Chelsea Guide</span>
                <h3 className="text-base font-semibold text-primary mt-1 group-hover:text-accent transition-colors">Secondary Glazing in Chelsea: Elegance and Silence in SW3</h3>
                <p className="text-sm text-muted-foreground mt-1">Cadogan Estate compliance, RBKC planning, and King's Road noise solutions.</p>
              </div>
            </Link>
            <Link to="/heritage-preservation-secondary-glazing" className="group block rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow bg-background border border-border">
              <div className="h-44 overflow-hidden">
                <img src="/lovable-uploads/combination_secondary_glazing.jpg" alt="Heritage-approved secondary glazing on a listed London property" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" width="400" height="300" loading="lazy" decoding="async" />
              </div>
              <div className="p-4">
                <span className="text-xs font-semibold text-accent uppercase tracking-wider">Heritage Guide</span>
                <h3 className="text-base font-semibold text-primary mt-1 group-hover:text-accent transition-colors">Heritage & Listed Building Glazing Solutions</h3>
                <p className="text-sm text-muted-foreground mt-1">Conservation-approved solutions for Grade I and II listed properties across London.</p>
              </div>
            </Link>
            <Link to="/noise-reduction-secondary-glazing" className="group block rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow bg-background border border-border">
              <div className="h-44 overflow-hidden">
                <img src="/lovable-uploads/acoustic_glass_cross_section.jpg" alt="Acoustic secondary glazing reducing traffic noise in a London home" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" width="400" height="225" loading="lazy" decoding="async" />
              </div>
              <div className="p-4">
                <span className="text-xs font-semibold text-accent uppercase tracking-wider">Acoustic Guide</span>
                <h3 className="text-base font-semibold text-primary mt-1 group-hover:text-accent transition-colors">Noise Reduction Secondary Glazing Guide</h3>
                <p className="text-sm text-muted-foreground mt-1">Reduce noise by up to 80% with specialist acoustic glazing for London homes.</p>
              </div>
            </Link>
          </div>
        </div>
      </section>
      
      {/* Product Types Section */}
      <ProductTypesSection />
      
      <Suspense fallback={<div className="h-96 flex items-center justify-center">Loading service area map...</div>}>
        <ServiceAreaMap />
        <div className="container mx-auto px-4 pb-8 text-center">
          <Link to="/locations">
            <Button variant="outline" className="mt-4">Browse All Service Locations</Button>
          </Link>
        </div>
      </Suspense>
      
      <Suspense fallback={<div className="h-96 flex items-center justify-center">Loading quote calculator...</div>}>
        <div id="quote-calculator">
          <QuoteCalculator />
        </div>
      </Suspense>
      <Suspense fallback={<div className="h-96 flex items-center justify-center">Loading testimonials...</div>}>
        <Testimonials />
        <div className="container mx-auto px-4 pb-12 text-center">
          <Link to="/blog-secondary-glazing">
            <Button className="mt-4 bg-primary hover:bg-primary-dark text-zinc-50">Read Our Latest Articles</Button>
          </Link>
        </div>
      </Suspense>
      
        <div className="container mx-auto px-4 pb-8">
          <ExternalResources showCaseStudies={true} showTechnical={false} showBenefits={true} />
        </div>
        
        <div className="container mx-auto px-4 pb-8">
          <InternalLinkingBoost currentPage="/" />
        </div>
        
        <Footer />
        
      </AIContentMarkup>
    </main>
  );
}
